// Team dashboard — the "Daily output" page (2026-08-24 redesign).
//
// One site-level "Orders out today" summary card, then six
// equal-height department cards (Goods In, Put Away, Shelf Drop,
// Picking, Sorting, Dispatch). Newport and Poland get the SORTER layout
// instead (`layout` prop, 2026-10-20): a race per sorter station against
// a target finish, orders out without a due figure, and Goods In / Put
// Away / Sorter 1 / Sorter 2 / Dispatch / Pallets Created cards — the
// weekly section mirrors it. Each card shares one anatomy: title, a headline
// day-average figure with a "vs same point yesterday" delta, an hourly
// activity histogram over the shared scanning day, then its own detail
// rows / hot-cold tiles. Colour has ONE job here — the brand orange
// carries "activity"; departments are not hue-coded.
//
// No page header of its own: the Team/Individual toggle and the
// warehouse filter live in DashboardAdmin's shared row above (per
// Alex — same position as on the Individual view).
//
// Presentation only: every number arrives from GET /admin/dashboard/team
// (fetched by DashboardAdmin's team poll). The chart is flexed divs —
// no charting library.
import { CSSProperties, ReactNode } from "react";
import { Truck, Clock, AlertTriangle, CheckCircle2, Trophy } from "lucide-react";
import { useTranslation } from "../i18n";
import { TeamOverview, TeamKpis, TeamWeek, TeamSorter, TeamWeekDept, EMPTY_TEAM } from "../api/dashboardActivity";
import DashboardRace from "./DashboardRace";
import DashboardSorterRace from "./DashboardSorterRace";
import { DashboardLayout } from "./helpers/dashboardLayout";

// The KPI date-range control lives in DashboardAdmin's shared toggle
// row (right-hand side), next to the warehouse filter — this component
// only renders the figures for the range it's handed.
interface DashboardTeamProps {
    team: TeamOverview;
    loaded: boolean;
    /** "picking" (Pembroke, all) or "sorter" (Newport, Poland) — see helpers/dashboardLayout.ts. */
    layout: DashboardLayout;
    kpis: TeamKpis;
    kpisLoaded: boolean;
    week: TeamWeek;
    weekLoaded: boolean;
    /**
     * Orders Shipped by date (Poland, 2026-10-23): rendered under the
     * Warehouse KPIs when DashboardAdmin hands one in (it owns its own
     * range + fetch — see DashboardShippedByDate); nothing otherwise.
     */
    shippedByDate?: ReactNode;
}

// "Mon 17" for the chart axis / bar titles.
const dayLabel = (iso: string) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric" });

const hourLabel = (h: number) => `${String(h).padStart(2, "0")}:00`;
const fmtInt = (n: number) => n.toLocaleString();
const sum = (xs: number[]) => xs.reduce((s, x) => s + x, 0);

// "+12%" / "−8%" against the same point yesterday; null when yesterday
// has no basis (never fabricate a percentage from zero).
const deltaPct = (today: number, yesterday: number): string | null => {
    if (yesterday <= 0) return null;
    const pct = Math.round(((today - yesterday) / yesterday) * 100);
    return `${pct >= 0 ? "+" : "−"}${Math.abs(pct)}%`;
};

// Progress-bar colour = the whole fill in ONE colour read off the %:
// red at 0 → amber at 50 → green at 100 (per Alex, 2026-08-25). The
// endpoints are the page's delta-down/-up tokens so the bar shares the
// deltas' vocabulary; the % label beside it carries the meaning for
// red-green colourblind viewers.
const PROGRESS_STOPS: Array<[number, [number, number, number]]> = [
    [0, [0xef, 0x44, 0x44]],   // --color-price      #ef4444
    [50, [0xf5, 0x9e, 0x0b]],  // amber              #f59e0b
    [100, [0x0a, 0x8f, 0x3c]], // --color-delta-up   #0a8f3c
];
export const progressColour = (pct: number): string => {
    const p = Math.max(0, Math.min(100, pct));
    let i = 0;
    while (i < PROGRESS_STOPS.length - 2 && p > PROGRESS_STOPS[i + 1][0]) i++;
    const [p0, c0] = PROGRESS_STOPS[i];
    const [p1, c1] = PROGRESS_STOPS[i + 1];
    const t = (p - p0) / (p1 - p0);
    const ch = (k: 0 | 1 | 2) => Math.round(c0[k] + (c1[k] - c0[k]) * t);
    return `rgb(${ch(0)}, ${ch(1)}, ${ch(2)})`;
};

const CHART_BAR_MAX_PX = 34;
// KPI cards: chart height (the rows under it were dropped so it can
// breathe) and the on-time target the dashed line marks.
const KPI_CHART_PX = 150;
const ON_TIME_TARGET_PCT = 98;

// On-time rate bands (per Alex, 2026-08-25) — the headline AND every
// day bar use the same rule: < 95 red + warning, 95–97.99 amber +
// warning, 98–99.9 green + tick, a perfect 100 green + trophy. Pass
// 100 ONLY for a genuinely perfect day/period (every order on time) —
// callers clamp a rounded 99.96 to 99.9 so the trophy is never won by
// rounding.
export type OnTimeBand = { colour: string; icon: "warning" | "tick" | "trophy" };
export const onTimeBand = (pct: number): OnTimeBand => {
    if (pct >= 100) return { colour: "#0a8f3c", icon: "trophy" };
    if (pct >= 98) return { colour: "#0a8f3c", icon: "tick" };
    if (pct >= 95) return { colour: "#f59e0b", icon: "warning" };
    return { colour: "#ef4444", icon: "warning" };
};
// 1-dp rate that only ever reads 100 when on_time === total.
export const onTimePct = (onTime: number, total: number): number | null => {
    if (total <= 0) return null;
    if (onTime >= total) return 100;
    return Math.min(99.9, Math.round((onTime / total) * 1000) / 10);
};
const STATUS_ICON_PX = 30;
export const BandIcon = ({ band }: { band: OnTimeBand }) => {
    const props = { size: STATUS_ICON_PX, color: band.colour, className: "tdash-kpi-status", strokeWidth: 2.2, "aria-hidden": true as const };
    if (band.icon === "trophy") return <Trophy {...props} />;
    if (band.icon === "tick") return <CheckCircle2 {...props} />;
    return <AlertTriangle {...props} />;
};
const CHART_STUB_PX = 6;

// One bar per hour across the whole scanning day. Hours still ahead
// (and empty past hours) render as low stubs so the axis stays full
// width and every card keeps the same height — the row is never hidden.
const HourlyChart = ({ buckets, dayStart, dayEnd, currentHour }: {
    buckets: number[];
    dayStart: number;
    dayEnd: number;
    currentHour: number;
}) => {
    const max = Math.max(1, ...buckets);
    return (
        <div className="tdash-chart">
            <div className="tdash-chart-bars">
                {buckets.map((v, i) => {
                    const hour = dayStart + i;
                    const isNow = hour === currentHour;
                    const future = !isNow && hour > currentHour;
                    const px = !future && v > 0
                        ? Math.max(CHART_STUB_PX, Math.round((v / max) * CHART_BAR_MAX_PX))
                        : CHART_STUB_PX;
                    const cls = isNow
                        ? "tdash-bar tdash-bar--now"
                        : !future && v > 0
                            ? "tdash-bar tdash-bar--past"
                            : "tdash-bar tdash-bar--stub";
                    return (
                        <div
                            key={hour}
                            className={cls}
                            style={{ height: `${px}px` }}
                            title={`${hourLabel(hour)} · ${fmtInt(v)}`}
                        />
                    );
                })}
            </div>
            <div className="tdash-chart-axis">
                <span>{hourLabel(dayStart)}</span>
                <span>{hourLabel(dayEnd)}</span>
            </div>
        </div>
    );
};

// One bar per DAY of the KPI range (same anatomy as the hourly chart,
// but sized by the card: the KPI cards give it the room the detail
// rows used to take). Days after today render as stubs; today is
// highlighted; a colour callback lets the on-time chart paint each
// day by its own rate; `scaleMax` pins the axis (0–100 for a percent
// chart) so a dashed `target` line sits where it should.
// Exported for DashboardLeadersBonus, which reuses the card chart and band icon.
export const DailyChart = ({ points, today, colour, heightPx = CHART_BAR_MAX_PX, scaleMax, target, axisLabels }: {
    // `tip` = the stats line shown in the hover tooltip under the day label.
    // `muted` = draw the bar grey whatever `colour` says (Leaders Bonus:
    // a non-working day's units count but aren't scored against a target).
    points: Array<{ date: string; value: number | null; tip: string; muted?: boolean }>;
    today: string;
    colour?: (value: number) => string;
    heightPx?: number;
    scaleMax?: number;
    target?: { value: number; label: string };
    /** One label per bar, centred under it (e.g. M T W T F S) — replaces the end labels. */
    axisLabels?: string[];
}) => {
    const max = scaleMax ?? Math.max(1, ...points.map((p) => p.value ?? 0));
    const barsStyle: CSSProperties = { height: `${heightPx}px` };
    if (points.length === 0) {
        return (
            <div className="tdash-chart">
                <div className="tdash-chart-bars" style={barsStyle} />
                <div className="tdash-chart-axis"><span>—</span><span>—</span></div>
            </div>
        );
    }
    return (
        <div className="tdash-chart">
            <div className="tdash-chart-bars" style={barsStyle}>
                {target && (
                    <div
                        className="tdash-chart-target"
                        style={{ bottom: `${Math.round((Math.min(target.value, max) / max) * heightPx)}px` }}
                    >
                        <span className="tdash-chart-target-label">{target.label}</span>
                    </div>
                )}
                {points.map((p, i) => {
                    const isNow = p.date === today;
                    const future = p.date > today;
                    const has = !future && p.value !== null && p.value > 0;
                    const px = has
                        ? Math.max(CHART_STUB_PX, Math.round(((p.value as number) / max) * heightPx))
                        : CHART_STUB_PX;
                    const cls = has
                        ? (isNow ? "tdash-bar tdash-bar--now" : "tdash-bar tdash-bar--past")
                        : "tdash-bar tdash-bar--stub";
                    const style: CSSProperties = { height: `${px}px` };
                    if (has && p.muted) style.backgroundColor = "var(--color-placeholder)";
                    else if (has && colour) style.backgroundColor = colour(p.value as number);
                    // Hover anywhere in the day's column (full chart height)
                    // to get the tooltip; edge columns pin it inside the card.
                    const tipCls = "tdash-bar-tip"
                        + (i === 0 ? " tdash-bar-tip--start" : i === points.length - 1 ? " tdash-bar-tip--end" : "");
                    return (
                        <div key={p.date} className="tdash-bar-col">
                            <div className={cls} style={style}>
                                <div className={tipCls} role="tooltip">
                                    <span className="tdash-bar-tip-head">{dayLabel(p.date)}</span>
                                    <span className="tdash-bar-tip-body">{p.tip}</span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            {axisLabels ? (
                <div className="tdash-chart-axis tdash-chart-axis--bars">
                    {axisLabels.map((l, i) => <span key={`${i}:${l}`}>{l}</span>)}
                </div>
            ) : (
                <div className="tdash-chart-axis">
                    <span>{dayLabel(points[0].date)}</span>
                    <span>{dayLabel(points[points.length - 1].date)}</span>
                </div>
            )}
        </div>
    );
};

// "M T W T F S" — the locale's narrow weekday letter for a date.
const dayLetter = (iso: string) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { weekday: "narrow" });

// A long headline line ("12,345 units/hr") steps the numeral down so it
// stays on one line at card width. Seven-up cards are ~143px wide at
// 1440 (2026-09-08), so the thresholds are short; the CSS also lets the
// unit wrap under the number as the last resort, never clipping.
const headlineSizeClass = (text: string) =>
    text.length >= 17 ? " tdash-headline--tight" : text.length >= 14 ? " tdash-headline--compact" : "";

// "Picking due today" stages, in pipeline order. Every open stage is the
// same neutral grey and only Fulfilled is green (Alex, 2026-09-09 — the
// department accents made the card read like six competing signals).
type PickingStage = "unfulfilled" | "picking" | "sorting" | "dispatch" | "flagged" | "fulfilled";
const STAGE_OPEN_COLOUR = "#9ca3af";
export const PICKING_STAGES: Array<{ key: PickingStage; labelKey: string; colour: string }> = [
    { key: "unfulfilled", labelKey: "status_unfulfilled", colour: STAGE_OPEN_COLOUR },
    { key: "picking", labelKey: "status_picking", colour: STAGE_OPEN_COLOUR },
    { key: "sorting", labelKey: "status_sorting", colour: STAGE_OPEN_COLOUR },
    { key: "dispatch", labelKey: "status_dispatch", colour: STAGE_OPEN_COLOUR },
    { key: "flagged", labelKey: "status_flagged", colour: STAGE_OPEN_COLOUR },
    { key: "fulfilled", labelKey: "status_fulfilled", colour: "#0a8f3c" },
];

// The shares the card draws: the six stages plus "late" (packed after
// the 15:30 cut-off — due but not out, shown as a red segment on the
// Fulfilled bar, mirroring the orders card's out-vs-due rule).
export type StageShare = PickingStage | "late";
const SHARE_KEYS: StageShare[] = [...PICKING_STAGES.map((s) => s.key), "late"];

// Whole-number percentages for the shares that ALWAYS sum to 100
// (largest-remainder method), with one rule on top (Alex, 2026-09-08):
// Fulfilled is floored, so it reads 100% only when every due item has
// actually shipped before the cut-off — the leftover points go to the
// other shares by largest fractional remainder (pipeline order on ties),
// so a small open stage shows 1% rather than vanishing. Plain rounding
// let 99.6% shipped read "100%" beside a 3% Sorting bar; floor-one /
// ceil-the-rest would overshoot (up to 105%).
export const stagePercents = (
    items: Record<StageShare, number>,
    total: number,
): Record<StageShare, number> => {
    const out = Object.fromEntries(SHARE_KEYS.map((k) => [k, 0])) as Record<StageShare, number>;
    if (total <= 0) return out;
    const exact = SHARE_KEYS.map((k) => (Math.max(0, items[k] ?? 0) / total) * 100);
    SHARE_KEYS.forEach((k, i) => { out[k] = Math.floor(exact[i]); });
    let left = 100 - SHARE_KEYS.reduce((s, k) => s + out[k], 0);
    // Only shares holding items can take a point; Fulfilled never does
    // unless nothing else is open.
    const takers = SHARE_KEYS
        .map((k, i) => ({ k, rem: exact[i] - Math.floor(exact[i]), i }))
        .filter((x) => x.k !== "fulfilled" && (items[x.k] ?? 0) > 0)
        .sort((a, b) => b.rem - a.rem || a.i - b.i);
    for (let n = 0; left > 0 && takers.length > 0; n = (n + 1) % takers.length) {
        out[takers[n].k] += 1;
        left -= 1;
    }
    if (left > 0) out.fulfilled += left; // everything shipped: 100 exactly
    // An open stage that holds items never reads 0% (Alex, 2026-09-08:
    // 7 items at Dispatch showed 0% while Fulfilled looked complete). Fund
    // the point from "late" first (text-only, no bar), else from Fulfilled.
    for (const k of SHARE_KEYS) {
        if (k === "fulfilled" || k === "late" || (items[k] ?? 0) <= 0 || out[k] > 0) continue;
        const donor = out.late > 1 ? "late" : out.fulfilled > 0 ? "fulfilled" : null;
        if (!donor) break;
        out[donor] -= 1;
        out[k] = 1;
    }
    return out;
};

const DashboardTeam = ({ team, loaded, layout, kpis, kpisLoaded, week, weekLoaded, shippedByDate }: DashboardTeamProps) => {
    const { t } = useTranslation();
    const { site } = team;
    const dayStart = site.day_start_hour;
    const dayEnd = site.day_end_hour;
    const cur = site.current_hour;

    const dispatched = site.orders_dispatched;
    const due = site.orders_due;
    const ordersDelta = deltaPct(dispatched, site.orders_dispatched_same_time_yesterday);

    const rate = (n: number | null) => (n !== null ? fmtInt(Math.round(n)) : "—");

    // One labelled progress bar for a delivery-type slice of the day's
    // orders: "Express  60 / 65  92%".
    const progressRow = (kind: "express" | "standard", label: string, done: number, dueN: number) => {
        const pct = dueN > 0 ? Math.round((done / dueN) * 100) : 0;
        return (
            <div className={`tdash-progress-row tdash-progress-row--${kind}`}>
                <div className="tdash-progress-meta">
                    <span className="tdash-progress-name">{label}</span>
                    <span className="tdash-progress-count">
                        {t("dashboard_team_progress_count", { done: fmtInt(done), due: fmtInt(dueN) })}
                    </span>
                    <span className="tdash-progress-pct">{pct}%</span>
                </div>
                <div className="tdash-progress-track">
                    <div
                        className="tdash-progress-fill"
                        style={{
                            width: `${Math.min(100, pct)}%`,
                            backgroundColor: progressColour(pct),
                        }}
                    />
                </div>
            </div>
        );
    };

    // One stage bar of the "Picking due today" card: "Sorting  120 / 1,000
    // 12%" — share of today's due ITEMS sitting at that stage, filled in
    // the stage's own colour.
    // `lateItems` (Fulfilled bar only): items packed after the cut-off —
    // TEXT only ("· 333 late"), never drawn on the bar, so Fulfilled's
    // fill stays at its true share and the bar can't read as complete
    // while work is still owed (Alex, 2026-09-08). Same out-vs-due split
    // the orders card makes, where the late orders are the unlabelled gap.
    const stageRow = (
        stage: PickingStage, label: string, items: number, total: number, pct: number, colour: string,
        lateItems = 0,
    ) => {
        return (
            <div key={stage} className={`tdash-progress-row tdash-progress-row--${stage}`}>
                <div className="tdash-progress-meta">
                    <span className="tdash-progress-name">{label}</span>
                    <span className="tdash-progress-count">
                        {/* Only Fulfilled carries "/ due" — the open stages
                            just show their item count (Alex, 2026-09-09). */}
                        {stage === "fulfilled"
                            ? t("dashboard_team_progress_count", { done: fmtInt(items), due: fmtInt(total) })
                            : fmtInt(items)}
                        {lateItems > 0 && (
                            <span className="tdash-progress-late">
                                {" · "}{fmtInt(lateItems)} {t("dashboard_team_late_word")}
                            </span>
                        )}
                    </span>
                    <span className="tdash-progress-pct">{pct}%</span>
                </div>
                <div className="tdash-progress-track">
                    <div
                        className="tdash-progress-fill"
                        style={{ width: `${Math.min(100, pct)}%`, backgroundColor: colour }}
                    />
                </div>
            </div>
        );
    };

    // Weekly scorecard row: "Express  675 / 783 on time  86% · 14%" over a
    // single bar split on-time (green) / late (red), per delivery type.
    const splitRow = (label: string, onTime: number, total: number) => {
        const late = Math.max(0, total - onTime);
        const pct = total > 0 ? Math.round((onTime / total) * 100) : 0;
        return (
            <div className="tdash-progress-row">
                <div className="tdash-progress-meta">
                    <span className="tdash-progress-name">{label}</span>
                    <span className="tdash-progress-count">
                        {t("dashboard_team_on_time_short", { on_time: fmtInt(onTime), total: fmtInt(total) })}
                    </span>
                    <span className="tdash-split-pcts">
                        <span className="tdash-delta--up">{total > 0 ? `${pct}%` : "—"}</span>
                        <span className="tdash-split-pcts-sep">·</span>
                        <span className="tdash-delta--down">{total > 0 ? `${100 - pct}%` : "—"}</span>
                    </span>
                </div>
                <div
                    className="tdash-split-bar"
                    role="img"
                    aria-label={`${label}: ${fmtInt(onTime)} ${t("dashboard_team_on_time_word")} / ${fmtInt(late)} ${t("dashboard_team_late_word")}`}
                >
                    <div className="tdash-split-bar-on" style={{ width: `${pct}%` }} />
                    <div className="tdash-split-bar-late" />
                </div>
            </div>
        );
    };

    const chart = (buckets: number[]) => (
        <HourlyChart
            buckets={buckets}
            dayStart={dayStart}
            dayEnd={dayEnd}
            currentHour={cur}
        />
    );

    // The delta line under a headline; the "no basis" state keeps the
    // slot so card zones stay aligned.
    const deltaLine = (today: number, yesterday: number) => {
        const d = deltaPct(today, yesterday);
        if (d === null) {
            return (
                <div className="tdash-delta tdash-delta--none">
                    {t("dashboard_team_vs_yesterday", { pct: "—" })}
                </div>
            );
        }
        return (
            <div className={`tdash-delta ${d.startsWith("−") ? "tdash-delta--down" : "tdash-delta--up"}`}>
                {t("dashboard_team_vs_yesterday", { pct: d })}
            </div>
        );
    };

    // Weekly variant of the delta line: "+N% vs last week".
    const weekDeltaLine = (thisWeek: number, lastWeek: number) => {
        const d = deltaPct(thisWeek, lastWeek);
        if (d === null) {
            return (
                <div className="tdash-delta tdash-delta--none">
                    {t("dashboard_team_vs_last_week", { pct: "—" })}
                </div>
            );
        }
        return (
            <div className={`tdash-delta ${d.startsWith("−") ? "tdash-delta--down" : "tdash-delta--up"}`}>
                {t("dashboard_team_vs_last_week", { pct: d })}
            </div>
        );
    };

    // One bar per day of the week (Mon→Sat) on the daily-chart primitive:
    // today highlighted, days still ahead as stubs. `tipOf` overrides the
    // default "N units" tooltip line (Put Away shows totes AND units).
    const weekChart = (daily: number[], unitKey: string, tipOf?: (i: number) => string) => (
        <DailyChart
            today={week.week.today || new Date().toISOString().slice(0, 10)}
            points={week.week.days.map((date, i) => ({
                date,
                value: daily[i] ?? 0,
                tip: tipOf ? tipOf(i) : `${fmtInt(daily[i] ?? 0)} ${t(unitKey)}`,
            }))}
            axisLabels={week.week.days.map(dayLetter)}
        />
    );
    // Put Away tooltip: "12 totes · 340 units" for the day.
    const putAwayTip = (totes: number, units: number) =>
        `${fmtInt(totes)} ${t("dashboard_team_totes")} · ${t("dashboard_team_units_count", { n: fmtInt(units) })}`;

    const headline = (value: string, unit: string, extra?: string) => (
        <div className={`tdash-headline${headlineSizeClass(`${value} ${unit}${extra ? ` ${extra}` : ""}`)}`}>
            <span className="tdash-headline-num">{value}</span>
            <span className="tdash-headline-unit">{unit}</span>
            {extra !== undefined && <span className="tdash-headline-extra">{extra}</span>}
        </div>
    );

    const detailRow = (label: string, value: string, highlight = false) => (
        <div className={`tdash-row${highlight ? " tdash-row--strip" : ""}`}>
            <span className="tdash-row-label">{label}</span>
            <span className="tdash-row-value">{value}</span>
        </div>
    );

    // Two label/value pairs sharing ONE row — Put Away's "SKUs · New
    // SKUs" sit in the detail-row zone at Goods In's single-row height,
    // so the six cards keep their size (per Alex, 2026-08-27).
    const pairRow = (a: [string, string], b: [string, string]) => (
        <div className="tdash-row tdash-row--pair">
            {[a, b].map(([label, value]) => (
                <span key={label} className="tdash-row-cell">
                    <span className="tdash-row-label">{label}</span>
                    <span className="tdash-row-value">{value}</span>
                </span>
            ))}
        </div>
    );

    const tile = (label: string, main: string, sub?: string) => (
        <div className="tdash-tile">
            <span className="tdash-tile-label">{label}</span>
            <span className="tdash-tile-value">{main}</span>
            {sub !== undefined && <span className="tdash-tile-sub">{sub}</span>}
        </div>
    );

    const gi = team.goods_in;
    const pa = team.putaway;
    const pk = team.picking;
    const fd = team.forecast_drop;
    const pdue = team.picking_due;
    const so = team.sorting;
    const dp = team.dispatch;
    const paQuiet = loaded && pa.totes_today === 0;
    const stageItems: Record<StageShare, number> = {
        unfulfilled: pdue.items_unfulfilled,
        picking: pdue.items_picking,
        sorting: pdue.items_sorting,
        dispatch: pdue.items_dispatch,
        flagged: pdue.items_flagged,
        fulfilled: pdue.items_fulfilled,
        late: pdue.items_late,
    };
    const stagePcts = stagePercents(stageItems, pdue.items_due);
    // "Ready to pick" ring. The fallback covers a BE snapshot from before
    // the block existed. The % is floored, so it reads 100% only when
    // every waiting unit is on a ready order.
    // Not-ready units split amber (down low, waiting on the order's other
    // lines) / red (up high or short). A BE snapshot without the split
    // reads everything not ready as red.
    const rdy = team.picking_ready ?? EMPTY_TEAM.picking_ready;
    const readyTotal = rdy.units_ready + rdy.units_not_ready;
    const readyPct = readyTotal > 0 ? Math.floor((rdy.units_ready / readyTotal) * 100) : 0;
    const readyLowWaiting = rdy.units_low_waiting ?? 0;
    const readyHigh = rdy.units_high ?? Math.max(0, rdy.units_not_ready - readyLowWaiting);
    const readyArc = readyTotal > 0 ? (rdy.units_ready / readyTotal) * 100 : 0;
    const readyAmberArc = readyTotal > 0 ? ((rdy.units_ready + readyLowWaiting) / readyTotal) * 100 : 0;

    // Weekly section figures.
    const wk = week.site;
    const wgi = week.goods_in;
    const wpa = week.putaway;
    const wsd = week.shelf_drop;
    const wfd = week.forecast_drop;
    const wpk = week.picking;
    const wso = week.sorting;
    const wdp = week.dispatch;
    const wpaQuiet = weekLoaded && wpa.totes === 0;
    const weekOnTimePct = wk.orders_dispatched > 0
        ? Math.round((wk.orders_on_time / wk.orders_dispatched) * 100) : 0;

    const ot = kpis.on_time;
    const tts = kpis.time_to_ship;
    const otQuiet = kpisLoaded && ot.total === 0;
    const otPct = onTimePct(ot.on_time, ot.total);
    const ttsQuiet = kpisLoaded && tts.orders === 0;
    const kpiToday = kpis.today || new Date().toISOString().slice(0, 10);
    const fmt1 = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

    // ── Cards shared by both layouts ──
    const goodsInCard = () => (
        <section className="tdash-card">
            <h3 className="tdash-card-title">{t("goods_in")}</h3>
            <div className="tdash-headline-block">
                {headline(rate(gi.units_per_hour), t("dashboard_team_units_hr"))}
                {deltaLine(sum(gi.hourly), gi.yesterday_units)}
            </div>
            {chart(gi.hourly)}
            <div className="tdash-rows">
                {detailRow(t("dashboard_team_booked_in"), fmtInt(gi.units))}
            </div>
            <div className="tdash-tiles">
                {tile(t("dashboard_team_hot"), fmtInt(gi.hot_units))}
                {tile(t("dashboard_team_cold"), fmtInt(gi.cold_units))}
                {(gi.sourcing_units ?? 0) > 0 && tile(t("sourcing"), fmtInt(gi.sourcing_units ?? 0))}
            </div>
        </section>
    );
    const putAwayCard = () => (
        <section className="tdash-card">
            <h3 className="tdash-card-title">{t("put_away")}</h3>
            <div className="tdash-headline-block">
                {/* The units extra sits on its own line (CSS
                    flex-basis 100%), so only the totes line
                    drives the size class. */}
                <div className={`tdash-headline${paQuiet ? " tdash-headline--quiet" : ""}${headlineSizeClass(`${fmtInt(pa.totes_today)} totes`)}`}>
                    <span className="tdash-headline-num">{fmtInt(pa.totes_today)}</span>
                    <span className="tdash-headline-unit">{t("dashboard_team_totes")}</span>
                    <span className="tdash-headline-extra">
                        {t("dashboard_team_units_count", { n: fmtInt(pa.units_today) })}
                    </span>
                </div>
                {paQuiet ? (
                    <div className="tdash-delta tdash-delta--none">
                        {t("dashboard_team_no_activity")}
                    </div>
                ) : deltaLine(pa.totes_today, pa.yesterday_totes)}
            </div>
            {chart(pa.hourly)}
            <div className="tdash-rows">
                {pairRow(
                    [t("dashboard_team_skus"), fmtInt(pa.skus_today)],
                    [t("dashboard_team_new_skus"), fmtInt(pa.new_skus_today)],
                )}
            </div>
            <div className="tdash-tiles">
                {tile(
                    t("dashboard_team_hot"),
                    fmtInt(pa.hot_done_today),
                    t("dashboard_team_units_count", { n: fmtInt(pa.hot_units_today) }),
                )}
                {tile(
                    t("dashboard_team_cold"),
                    fmtInt(pa.cold_done_today),
                    t("dashboard_team_units_count", { n: fmtInt(pa.cold_units_today) }),
                )}
            </div>
        </section>
    );
    const dispatchCard = () => (
        <section className="tdash-card">
            <h3 className="tdash-card-title">{t("dispatch")}</h3>
            <div className="tdash-headline-block">
                {headline(rate(dp.units_per_hour), t("dashboard_team_units_hr"))}
                {deltaLine(sum(dp.hourly), dp.yesterday_units)}
            </div>
            {chart(dp.hourly)}
            <div className="tdash-rows">
                {detailRow(t("dashboard_team_units_dispatched"), fmtInt(dp.units))}
                {/* Same figure as the hero (customer orders incl.
                    consolidation children, placed before cut-off) —
                    one "orders" number on the page (per Alex,
                    2026-08-26). Parcels live in the Waves rows. */}
                {detailRow(t("dashboard_team_orders_dispatched"), fmtInt(dispatched))}
            </div>
        </section>
    );
    const weekGoodsInCard = () => (
        <section className="tdash-card">
            <h3 className="tdash-card-title">{t("goods_in")}</h3>
            <div className="tdash-headline-block">
                {headline(rate(wgi.units_per_hour), t("dashboard_team_units_hr"))}
                {weekDeltaLine(wgi.units, wgi.last_week_units)}
            </div>
            {weekChart(wgi.daily, "dashboard_col_units")}
            <div className="tdash-rows">
                {detailRow(t("dashboard_team_booked_in"), fmtInt(wgi.units))}
            </div>
            <div className="tdash-tiles">
                {tile(t("dashboard_team_hot"), fmtInt(wgi.hot_units))}
                {tile(t("dashboard_team_cold"), fmtInt(wgi.cold_units))}
                {(wgi.sourcing_units ?? 0) > 0 && tile(t("sourcing"), fmtInt(wgi.sourcing_units ?? 0))}
            </div>
        </section>
    );
    const weekPutAwayCard = () => (
        <section className="tdash-card">
            <h3 className="tdash-card-title">{t("put_away")}</h3>
            <div className="tdash-headline-block">
                <div className={`tdash-headline${wpaQuiet ? " tdash-headline--quiet" : ""}${headlineSizeClass(`${fmtInt(wpa.totes)} totes`)}`}>
                    <span className="tdash-headline-num">{fmtInt(wpa.totes)}</span>
                    <span className="tdash-headline-unit">{t("dashboard_team_totes")}</span>
                    <span className="tdash-headline-extra">
                        {t("dashboard_team_units_count", { n: fmtInt(wpa.units) })}
                    </span>
                </div>
                {wpaQuiet ? (
                    <div className="tdash-delta tdash-delta--none">
                        {t("dashboard_team_no_activity")}
                    </div>
                ) : weekDeltaLine(wpa.totes, wpa.last_week_totes)}
            </div>
            {weekChart(wpa.daily, "dashboard_team_totes", (i) => putAwayTip(wpa.daily[i] ?? 0, wpa.daily_units?.[i] ?? 0))}
            <div className="tdash-rows">
                {pairRow(
                    [t("dashboard_team_skus"), fmtInt(wpa.skus)],
                    [t("dashboard_team_new_skus"), fmtInt(wpa.new_skus)],
                )}
            </div>
            <div className="tdash-tiles">
                {tile(
                    t("dashboard_team_hot"),
                    fmtInt(wpa.hot_totes),
                    t("dashboard_team_units_count", { n: fmtInt(wpa.hot_units) }),
                )}
                {tile(
                    t("dashboard_team_cold"),
                    fmtInt(wpa.cold_totes),
                    t("dashboard_team_units_count", { n: fmtInt(wpa.cold_units) }),
                )}
            </div>
        </section>
    );
    const weekDispatchCard = () => (
        <section className="tdash-card">
            <h3 className="tdash-card-title">{t("dispatch")}</h3>
            <div className="tdash-headline-block">
                {headline(rate(wdp.units_per_hour), t("dashboard_team_units_hr"))}
                {weekDeltaLine(wdp.units, wdp.last_week_units)}
            </div>
            {weekChart(wdp.daily, "dashboard_col_units")}
            <div className="tdash-rows">
                {detailRow(t("dashboard_team_units_dispatched"), fmtInt(wdp.units))}
                {detailRow(t("dashboard_team_orders_dispatched"), fmtInt(wk.orders_dispatched))}
            </div>
        </section>
    );

    // ── Sorter layout (Newport, Poland — 2026-10-20) ──
    const sorterLayout = layout === "sorter";
    const s1 = team.sorter_1 ?? null;
    const s2 = team.sorter_2 ?? null;
    const pc = team.pallets_created ?? null;
    const ws1 = week.sorter_1;
    const ws2 = week.sorter_2;
    const wpc = week.pallets_created;
    const emptyHourly = Array<number>(Math.max(0, dayEnd - dayStart)).fill(0);
    const emptyDaily = Array<number>(6).fill(0);

    // Orders out with no due figure (decision 6): the shipped count, the
    // express / standard split as plain counts, and the comparison.
    const shippedCard = (period: "day" | "week") => {
        const shipped = period === "day" ? dispatched : wk.orders_dispatched;
        const express = period === "day" ? site.orders_dispatched_express : wk.orders_dispatched_express;
        const standard = period === "day" ? site.orders_dispatched_standard : wk.orders_dispatched_standard;
        return (
            <section className="tdash-orders-card tdash-orders-card--shipped">
                <div className="tdash-orders-main">
                    <span className="tdash-label">
                        {t(period === "day" ? "dashboard_team_orders_out_today" : "dashboard_team_orders_out_week")}
                    </span>
                    <div className="tdash-hero">
                        <span className="tdash-hero-num">{fmtInt(shipped)}</span>
                        <span className="tdash-hero-due">{t("dashboard_team_orders_shipped_word")}</span>
                    </div>
                </div>
                <div className="tdash-orders-split">
                    {([["express", express], ["standard", standard]] as Array<[string, number]>).map(([kind, n]) => (
                        <span key={kind} className="tdash-row-cell">
                            <span className="tdash-row-label">{t(`delivery_type_${kind}`)}</span>
                            <span className="tdash-row-value">{fmtInt(n)}</span>
                        </span>
                    ))}
                </div>
                <div className="tdash-orders-yesterday">
                    {period === "day" ? (<>
                        <span className="tdash-caption">{t("dashboard_team_same_time_yesterday")}</span>
                        <div className="tdash-yesterday-value">
                            <span>{fmtInt(site.orders_dispatched_same_time_yesterday)}</span>
                            {ordersDelta !== null && (
                                <span className={`tdash-delta-pct ${ordersDelta.startsWith("−") ? "tdash-delta--down" : "tdash-delta--up"}`}>
                                    {ordersDelta}
                                </span>
                            )}
                        </div>
                    </>) : weekDeltaLine(wk.orders_dispatched, wk.orders_dispatched_same_point_last_week)}
                </div>
            </section>
        );
    };

    const sorterCard = (n: 1 | 2, st: TeamSorter | null) => (
        <section className="tdash-card">
            <h3 className="tdash-card-title">{t(n === 1 ? "sorter_1" : "sorter_2")}</h3>
            <div className="tdash-headline-block">
                {headline(rate(st?.items_per_hour ?? null), t("dashboard_team_units_hr"))}
                {deltaLine(st?.items ?? 0, st?.yesterday_items ?? 0)}
            </div>
            {chart(st?.hourly ?? emptyHourly)}
            <div className="tdash-rows">
                {detailRow(t("dashboard_team_items_sorted"), fmtInt(st?.items ?? 0))}
                {detailRow(t("dashboard_team_people_on_station"), fmtInt(st?.people_now ?? 0))}
            </div>
        </section>
    );

    const colourTiles = (counts: { red: number; yellow: number; grey: number; black: number } | null) => (
        <div className="tdash-tiles tdash-tiles--4">
            {(["red", "yellow", "grey", "black"] as const).map((c) => (
                <div key={c} className={`tdash-tile tdash-tile--${c}`}>
                    <span className="tdash-tile-label">{t(`colour_${c}`)}</span>
                    <span className="tdash-tile-value">{fmtInt(counts?.[c] ?? 0)}</span>
                </div>
            ))}
        </div>
    );

    const palletsCard = () => (
        <section className="tdash-card">
            <h3 className="tdash-card-title">{t("pallets_created")}</h3>
            <div className="tdash-headline-block">
                {headline(fmtInt(pc?.pallets ?? 0), t("dashboard_team_pallets"))}
                {deltaLine(pc?.pallets ?? 0, pc?.yesterday_pallets ?? 0)}
            </div>
            {chart(pc?.hourly ?? emptyHourly)}
            {colourTiles(pc)}
        </section>
    );

    const weekSorterCard = (n: 1 | 2, d: TeamWeekDept | undefined) => (
        <section className="tdash-card">
            <h3 className="tdash-card-title">{t(n === 1 ? "sorter_1" : "sorter_2")}</h3>
            <div className="tdash-headline-block">
                {headline(rate(d?.units_per_hour ?? null), t("dashboard_team_units_hr"))}
                {weekDeltaLine(d?.units ?? 0, d?.last_week_units ?? 0)}
            </div>
            {weekChart(d?.daily ?? emptyDaily, "dashboard_col_units")}
            <div className="tdash-rows">
                {detailRow(t("dashboard_team_items_sorted"), fmtInt(d?.units ?? 0))}
            </div>
        </section>
    );

    const weekPalletsCard = () => (
        <section className="tdash-card">
            <h3 className="tdash-card-title">{t("pallets_created")}</h3>
            <div className="tdash-headline-block">
                {headline(fmtInt(wpc?.units ?? 0), t("dashboard_team_pallets"))}
                {weekDeltaLine(wpc?.units ?? 0, wpc?.last_week_units ?? 0)}
            </div>
            {weekChart(wpc?.daily ?? emptyDaily, "dashboard_team_pallets")}
            {colourTiles(wpc ?? null)}
        </section>
    );

    return (
        <div className="tdash">
            {/* ── Warehouse KPIs (range-based; range picked in the toggle row) ── */}
            <h2 className="tdash-section-heading">{t("dashboard_team_kpis")}</h2>

            <div className={`tdash-kpi-grid${sorterLayout ? " tdash-kpi-grid--single" : ""}`}>
                {/* On Time Shipment Rate — not for the sorter layout (decision 7). */}
                {!sorterLayout && (
                <section className="tdash-card tdash-card--kpi">
                    <Truck className="tdash-kpi-icon" size={88} strokeWidth={1.4} aria-hidden="true" />
                    <h3 className="tdash-card-title">{t("dashboard_team_on_time_rate")}</h3>
                    <div className="tdash-headline-block">
                        <div className={`tdash-headline${otQuiet ? " tdash-headline--quiet" : ""}`}>
                            <span
                                className="tdash-headline-num"
                                style={otPct !== null ? { color: onTimeBand(otPct).colour } : undefined}
                            >
                                {otPct !== null ? fmt1(otPct) : "—"}
                            </span>
                            <span className="tdash-headline-unit">%</span>
                            {otPct !== null && <BandIcon band={onTimeBand(otPct)} />}
                        </div>
                        <div className="tdash-delta tdash-delta--none">
                            {otQuiet
                                ? t("dashboard_team_no_orders_in_range")
                                : t("dashboard_team_on_time_count", {
                                    on_time: fmtInt(ot.on_time), total: fmtInt(ot.total),
                                })}
                        </div>
                        <div className="tdash-caption">
                            {t("dashboard_team_dispatch_cutoff", { time: kpis.dispatch_cutoff })}
                        </div>
                    </div>
                    <DailyChart
                        today={kpiToday}
                        colour={(v) => onTimeBand(v).colour}
                        heightPx={KPI_CHART_PX}
                        scaleMax={100}
                        target={{
                            value: ON_TIME_TARGET_PCT,
                            label: t("dashboard_team_target", { pct: String(ON_TIME_TARGET_PCT) }),
                        }}
                        points={ot.daily.map((d) => {
                            const pct = onTimePct(d.on_time, d.total);
                            return {
                                date: d.date,
                                value: pct,
                                tip: pct !== null
                                    ? t("dashboard_team_tip_on_time", {
                                        shipped: fmtInt(d.on_time), due: fmtInt(d.total), pct: fmt1(pct),
                                    }) + (d.early > 0
                                        ? ` · ${t("dashboard_team_tip_early", { early: fmtInt(d.early) })}`
                                        : "")
                                    : "—",
                            };
                        })}
                    />
                </section>
                )}

                {/* Time to Ship */}
                <section className="tdash-card tdash-card--kpi">
                    <Clock className="tdash-kpi-icon" size={88} strokeWidth={1.4} aria-hidden="true" />
                    <h3 className="tdash-card-title">{t("dashboard_team_time_to_ship")}</h3>
                    <div className="tdash-headline-block">
                        <div className={`tdash-headline${ttsQuiet ? " tdash-headline--quiet" : ""}`}>
                            <span className="tdash-headline-num">
                                {tts.avg_hours !== null ? fmt1(tts.avg_hours) : "—"}
                            </span>
                            <span className="tdash-headline-unit">{t("dashboard_team_hours_unit")}</span>
                        </div>
                        <div className="tdash-delta tdash-delta--none">
                            {ttsQuiet
                                ? t("dashboard_team_no_orders_in_range")
                                : t("dashboard_team_orders_shipped_count", { n: fmtInt(tts.orders) })}
                        </div>
                    </div>
                    <DailyChart
                        today={kpiToday}
                        heightPx={KPI_CHART_PX}
                        points={tts.daily.map((d) => ({
                            date: d.date,
                            value: d.avg_hours,
                            tip: d.avg_hours !== null
                                ? `${fmt1(d.avg_hours)} ${t("dashboard_team_hours_unit")}`
                                : "—",
                        }))}
                    />
                </section>
            </div>

            {shippedByDate}

            {/* ── Daily KPIs: today's orders + per-department output ── */}
            <h2 className="tdash-section-heading">{t("dashboard_team_daily_kpis")}</h2>

            {/* Sorter layout (Newport, Poland — 2026-10-20): one race per
                station against its target finish, then orders out with no
                due figure. The picking layout keeps the race to the
                cut-off, the queue tiles and the picking cards below. */}
            {sorterLayout && (<>
                <DashboardSorterRace sorter={1} race={s1?.current} />
                <DashboardSorterRace sorter={2} race={s2?.current} />
                {shippedCard("day")}
            </>)}
            {!sorterLayout && (<>
            {/* Race to the cut-off — items + orders due today running to
                100%, finish-time estimate from the last 30 min of packing. */}
            <DashboardRace race={team.race} />

            {/* Orders out today + the two drop QUEUE tiles on one row (Alex,
                2026-09-10): what Shelf Drop still owes today's orders and
                what Forecast Drop still has queued, each with the closed
                totes waiting in that module's put-away queue. */}
            <div className="tdash-orders-row">
            <section className="tdash-orders-card">
                <div className="tdash-orders-main">
                    <span className="tdash-label">{t("dashboard_team_orders_out_today")}</span>
                    <div className="tdash-hero">
                        <span className="tdash-hero-num">{fmtInt(dispatched)}</span>
                        <span className="tdash-hero-due">
                            {t("dashboard_team_of_due", { n: fmtInt(due) })}
                        </span>
                    </div>
                </div>
                <div className="tdash-orders-progress">
                    {/* Express on top, standard below — the hero keeps
                        the overall figure; these are its two halves. */}
                    {progressRow("express", t("delivery_type_express"),
                        site.orders_dispatched_express, site.orders_due_express)}
                    {progressRow("standard", t("delivery_type_standard"),
                        site.orders_dispatched_standard, site.orders_due_standard)}
                </div>
                <div className="tdash-orders-yesterday">
                    <span className="tdash-caption">{t("dashboard_team_same_time_yesterday")}</span>
                    <div className="tdash-yesterday-value">
                        <span>{fmtInt(site.orders_dispatched_same_time_yesterday)}</span>
                        {ordersDelta !== null && (
                            <span className={`tdash-delta-pct ${ordersDelta.startsWith("−") ? "tdash-delta--down" : "tdash-delta--up"}`}>
                                {ordersDelta}
                            </span>
                        )}
                    </div>
                </div>
            </section>

            {/* Shelf Drop queue: units orders due today (or overdue) still
                need brought down — the home tile's "must ship today" read. */}
            <section className="tdash-queue-card">
                <span className="tdash-label">{t("dashboard_team_shelf_queue_title")}</span>
                <div className="tdash-hero">
                    <span className="tdash-hero-num">{fmtInt(pk.shelf_drop_units_due_today)}</span>
                    <span className="tdash-hero-due">{t("dashboard_team_units_to_drop_due_today")}</span>
                </div>
                <div className="tdash-rows">
                    {detailRow(t("dashboard_team_totes_to_put_away"), fmtInt(pk.shelf_drop_totes_awaiting_putaway))}
                </div>
            </section>

            {/* Forecast Drop queue: the whole current queue, no due filter. */}
            <section className="tdash-queue-card">
                <span className="tdash-label">{t("dashboard_team_forecast_queue_title")}</span>
                <div className="tdash-hero">
                    <span className="tdash-hero-num">{fmtInt(fd.units_to_drop)}</span>
                    <span className="tdash-hero-due">{t("dashboard_team_units_to_drop")}</span>
                </div>
                <div className="tdash-rows">
                    {detailRow(t("dashboard_team_totes_to_put_away"), fmtInt(fd.totes_awaiting_putaway))}
                </div>
            </section>
            </div>

            {/* Picking due today (per Alex, 2026-09-08): the orders card's
                population in ITEMS — same cut-off rules — split by the
                stage each order sits at. Hero = items past picking
                (sorting + dispatch + fulfilled) of items due. */}
            <div className="tdash-picking-row">
            <section className="tdash-orders-card tdash-orders-card--stages">
                <div className="tdash-orders-main">
                    <span className="tdash-label">{t("dashboard_team_picking_due_today")}</span>
                    <div className="tdash-hero">
                        {/* Headline = items PAST picking: sorting + dispatch +
                            fulfilled (+ packed late). Flagged is left out —
                            its own bar (Alex, 2026-09-09). */}
                        <span className="tdash-hero-num">
                            {fmtInt(pdue.items_sorting + pdue.items_dispatch + pdue.items_fulfilled + pdue.items_late)}
                        </span>
                        <span className="tdash-hero-due">
                            {t("dashboard_team_of_items", { n: fmtInt(pdue.items_due) })}
                        </span>
                    </div>
                </div>
                <div className="tdash-orders-progress tdash-orders-progress--stages">
                    {PICKING_STAGES.map((s) =>
                        stageRow(s.key, t(s.labelKey), stageItems[s.key], pdue.items_due, stagePcts[s.key], s.colour,
                            s.key === "fulfilled" ? pdue.items_late : 0))}
                </div>
            </section>

            {/* Ready to pick: units on waiting orders as a ring — green =
                units on orders that can be picked in full down low now,
                amber = units already down low whose order is still waiting
                on other lines, red = units still up high or short of
                stock. Drawn as three stacked arcs from 12 o'clock: the red
                track, the amber arc to ready+low-waiting, the green arc to
                ready. The ready figure sits inside the ring; the amber and
                red figures are listed beside it. */}
            <section className="tdash-ready-card" title={t("dashboard_team_ready_help")}>
                <span className="tdash-label">{t("dashboard_team_ready_to_pick")}</span>
                <div className="tdash-ready-body">
                <div
                    className="tdash-ready-ring"
                    role="img"
                    aria-label={`${t("dashboard_team_ready_to_pick")}: ${fmtInt(rdy.units_ready)} ${t("dashboard_team_ready_word")} / ${fmtInt(readyLowWaiting)} ${t("dashboard_team_low_waiting_word")} / ${fmtInt(readyHigh)} ${t("dashboard_team_high_word")}`}
                >
                    <svg viewBox="0 0 120 120" aria-hidden="true">
                        <circle
                            cx="60" cy="60" r="52"
                            className={`tdash-ready-ring-track${readyTotal > 0 ? " tdash-ready-ring-track--open" : ""}`}
                        />
                        {readyLowWaiting > 0 && (
                            <circle
                                cx="60" cy="60" r="52" pathLength={100}
                                className="tdash-ready-ring-fill tdash-ready-ring-fill--low"
                                strokeDasharray={`${readyAmberArc} 100`}
                            />
                        )}
                        {rdy.units_ready > 0 && (
                            <circle
                                cx="60" cy="60" r="52" pathLength={100}
                                className="tdash-ready-ring-fill"
                                strokeDasharray={`${readyArc} 100`}
                            />
                        )}
                    </svg>
                    <div className="tdash-ready-figures">
                        <span className="tdash-ready-num tdash-ready-num--ready">{fmtInt(rdy.units_ready)}</span>
                        <span className="tdash-ready-word">{t("dashboard_team_ready_word")}</span>
                    </div>
                </div>
                {/* The not-ready split listed beside the ring, one row
                    each: dot (matching its arc), number, word. */}
                <ul className="tdash-ready-legend" aria-hidden="true">
                    <li className="tdash-ready-legend-row tdash-ready-legend-row--low">
                        <span className="tdash-ready-dot tdash-ready-dot--low" />
                        <span className="tdash-ready-num tdash-ready-num--low">{fmtInt(readyLowWaiting)}</span>
                        <span className="tdash-ready-word">{t("dashboard_team_low_waiting_word")}</span>
                    </li>
                    <li className="tdash-ready-legend-row tdash-ready-legend-row--high">
                        <span className="tdash-ready-dot tdash-ready-dot--high" />
                        <span className="tdash-ready-num tdash-ready-num--not">{fmtInt(readyHigh)}</span>
                        <span className="tdash-ready-word">{t("dashboard_team_high_word")}</span>
                    </li>
                </ul>
                </div>
                <span className="tdash-caption">
                    {t("dashboard_team_ready_caption", {
                        pct: String(readyPct),
                        ready: fmtInt(rdy.orders_ready),
                        total: fmtInt(rdy.orders_ready + rdy.orders_not_ready),
                    })}
                </span>
            </section>
            </div>

            </>)}

            <div className="tdash-section-label">
                <span className="tdash-label">{t("dashboard_team_by_department")}</span>
                <span className="tdash-help">{sorterLayout ? t("dashboard_team_sorter_help") : t("dashboard_team_dept_help")}</span>
            </div>

            {sorterLayout && (
            <div className="tdash-grid tdash-grid--6">
                {goodsInCard()}
                {putAwayCard()}
                {sorterCard(1, s1)}
                {sorterCard(2, s2)}
                {dispatchCard()}
                {palletsCard()}
            </div>
            )}
            {!sorterLayout && (
            <div className="tdash-grid">
                {goodsInCard()}
                {putAwayCard()}
                {/* Forecast Drop (between Put Away and Shelf Drop, per Alex 2026-09-08) */}
                <section className="tdash-card">
                    <h3 className="tdash-card-title">{t("forecast_drop")}</h3>
                    <div className="tdash-headline-block">
                        {headline(rate(fd.units_per_hour), t("dashboard_team_units_hr"))}
                        {deltaLine(sum(fd.hourly), fd.yesterday_units)}
                    </div>
                    {chart(fd.hourly)}
                    <div className="tdash-rows">
                        {detailRow(t("dashboard_team_units_picked"), fmtInt(fd.units))}
                        {detailRow(t("dashboard_team_awaiting_putaway"), fmtInt(fd.awaiting_putaway))}
                    </div>
                </section>

                {/* Shelf Drop */}
                <section className="tdash-card">
                    <h3 className="tdash-card-title">{t("shelf_drop")}</h3>
                    <div className="tdash-headline-block">
                        {headline(rate(pk.shelf_drop_units_per_hour), t("dashboard_team_units_hr"))}
                        {deltaLine(sum(pk.shelf_drop_hourly), pk.shelf_drop_yesterday_units)}
                    </div>
                    {chart(pk.shelf_drop_hourly)}
                    <div className="tdash-rows">
                        {detailRow(t("dashboard_team_units_picked"), fmtInt(pk.shelf_drop_units))}
                        {detailRow(t("dashboard_team_awaiting_putaway"), fmtInt(pk.shelf_drop_awaiting_putaway))}
                    </div>
                </section>

                {/* Picking */}
                <section className="tdash-card">
                    <h3 className="tdash-card-title">{t("picking_admin_title")}</h3>
                    <div className="tdash-headline-block">
                        {headline(rate(pk.items_per_hour), t("dashboard_team_units_hr"))}
                        {deltaLine(sum(pk.hourly), pk.yesterday_items)}
                    </div>
                    {chart(pk.hourly)}
                    <div className="tdash-rows">
                        {detailRow(t("dashboard_team_items_picked"), fmtInt(pk.items_picked))}
                    </div>
                </section>

                {/* Sorting (P&S) */}
                <section className="tdash-card">
                    <h3 className="tdash-card-title">{t("zone_storage")}</h3>
                    <div className="tdash-headline-block">
                        {headline(rate(so.items_per_hour), t("dashboard_team_units_hr"))}
                        {deltaLine(sum(so.hourly), so.yesterday_items)}
                    </div>
                    {chart(so.hourly)}
                    <div className="tdash-rows">
                        {detailRow(t("dashboard_team_items_sorted"), fmtInt(so.items_sorted))}
                        {detailRow(t("dashboard_team_orders_sorted"), fmtInt(so.orders_sorted))}
                    </div>
                </section>
                {dispatchCard()}
            </div>
            )}

            {/* ── Weekly KPIs: the daily section consolidated Mon→Sat, one bar per day ── */}
            <h2 className="tdash-section-heading">
                {t("dashboard_team_weekly_kpis")}
                {week.week.days.length === 6 && (
                    <span className="tdash-section-sub">
                        {t("dashboard_team_week_range", {
                            from: dayLabel(week.week.days[0]),
                            to: dayLabel(week.week.days[5]),
                        })}
                    </span>
                )}
            </h2>

            {sorterLayout && shippedCard("week")}
            {/* Weekly orders card is a SCORECARD (per Alex, 2026-08-26):
                orders out this week, split on time vs late on ONE bar —
                unlike the daily card's shipped-vs-due progress view. */}
            {!sorterLayout && (
            <section className="tdash-orders-card">
                <div className="tdash-orders-main">
                    <span className="tdash-label">{t("dashboard_team_orders_out_week")}</span>
                    <div className="tdash-hero">
                        <span className="tdash-hero-num">{fmtInt(wk.orders_dispatched)}</span>
                        <span className="tdash-hero-due">
                            {wk.orders_dispatched > 0
                                ? t("dashboard_team_on_time_pct", { pct: String(weekOnTimePct) })
                                : "—"}
                        </span>
                    </div>
                </div>
                <div className="tdash-orders-progress">
                    {/* One split bar per delivery type (per Alex): green on
                        time vs red late, both percentages shown. */}
                    {splitRow(t("delivery_type_express"), wk.orders_on_time_express, wk.orders_dispatched_express)}
                    {splitRow(t("delivery_type_standard"), wk.orders_on_time_standard, wk.orders_dispatched_standard)}
                </div>
            </section>
            )}

            <div className="tdash-section-label">
                <span className="tdash-label">{t("dashboard_team_by_department")}</span>
                <span className="tdash-help">{t("dashboard_team_week_dept_help")}</span>
            </div>

            {sorterLayout && (
            <div className="tdash-grid tdash-grid--6">
                {weekGoodsInCard()}
                {weekPutAwayCard()}
                {weekSorterCard(1, ws1)}
                {weekSorterCard(2, ws2)}
                {weekDispatchCard()}
                {weekPalletsCard()}
            </div>
            )}
            {!sorterLayout && (
            <div className="tdash-grid">
                {weekGoodsInCard()}
                {weekPutAwayCard()}
                {/* Forecast Drop */}
                <section className="tdash-card">
                    <h3 className="tdash-card-title">{t("forecast_drop")}</h3>
                    <div className="tdash-headline-block">
                        {headline(rate(wfd.units_per_hour), t("dashboard_team_units_hr"))}
                        {weekDeltaLine(wfd.units, wfd.last_week_units)}
                    </div>
                    {weekChart(wfd.daily, "dashboard_col_units")}
                    <div className="tdash-rows">
                        {detailRow(t("dashboard_team_units_picked"), fmtInt(wfd.units))}
                        {detailRow(t("dashboard_team_awaiting_putaway"), fmtInt(wfd.awaiting_putaway))}
                    </div>
                </section>

                {/* Shelf Drop */}
                <section className="tdash-card">
                    <h3 className="tdash-card-title">{t("shelf_drop")}</h3>
                    <div className="tdash-headline-block">
                        {headline(rate(wsd.units_per_hour), t("dashboard_team_units_hr"))}
                        {weekDeltaLine(wsd.units, wsd.last_week_units)}
                    </div>
                    {weekChart(wsd.daily, "dashboard_col_units")}
                    <div className="tdash-rows">
                        {detailRow(t("dashboard_team_units_picked"), fmtInt(wsd.units))}
                        {detailRow(t("dashboard_team_awaiting_putaway"), fmtInt(wsd.awaiting_putaway))}
                    </div>
                </section>

                {/* Picking */}
                <section className="tdash-card">
                    <h3 className="tdash-card-title">{t("picking_admin_title")}</h3>
                    <div className="tdash-headline-block">
                        {headline(rate(wpk.units_per_hour), t("dashboard_team_units_hr"))}
                        {weekDeltaLine(wpk.units, wpk.last_week_units)}
                    </div>
                    {weekChart(wpk.daily, "dashboard_col_units")}
                    <div className="tdash-rows">
                        {detailRow(t("dashboard_team_items_picked"), fmtInt(wpk.units))}
                    </div>
                </section>

                {/* Sorting */}
                <section className="tdash-card">
                    <h3 className="tdash-card-title">{t("zone_storage")}</h3>
                    <div className="tdash-headline-block">
                        {headline(rate(wso.units_per_hour), t("dashboard_team_units_hr"))}
                        {weekDeltaLine(wso.units, wso.last_week_units)}
                    </div>
                    {weekChart(wso.daily, "dashboard_col_units")}
                    <div className="tdash-rows">
                        {detailRow(t("dashboard_team_items_sorted"), fmtInt(wso.units))}
                        {detailRow(t("dashboard_team_orders_sorted"), fmtInt(wso.orders_sorted))}
                    </div>
                </section>
                {weekDispatchCard()}
            </div>
            )}
        </div>
    );
};

export default DashboardTeam;
